import React, { useEffect, useState } from "react";
import { Panel, Field } from "@/pages/admin/shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Save, RefreshCw, Trash2, Eye, MousePointerClick, Smartphone } from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "@/pages/admin/api";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon size={13} /> {label}</div>
      <div className="font-heading font-bold text-2xl mt-1">{value}</div>
    </div>
  );
}

export default function AnalyticsSettings() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(30);
  const [saving, setSaving] = useState(false);

  const loadSummary = (d = days) =>
    api.get(`/analytics/summary?days=${d}`).then((r) => setSummary(r.data)).catch(() => {});

  useEffect(() => { api.get("/settings/analytics").then((r) => setData(r.data)); }, []);
  useEffect(() => { loadSummary(days); /* eslint-disable-next-line */ }, [days]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const set = (k, v) => setData({ ...data, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/analytics", data);
      setData(r.data);
      toast.success("Analytics settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Delete all internal analytics data?")) return;
    await api.delete("/analytics/reset");
    toast.success("Analytics data cleared");
    loadSummary();
  };

  const saveBtn = (
    <Button data-testid="save-analytics" onClick={save} disabled={saving} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
      <Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save Changes"}
    </Button>
  );

  return (
    <div className="space-y-6">
      <Panel
        title="Site Analytics"
        subtitle="Built-in traffic tracking — no third-party account needed."
        actions={
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <Button key={d} data-testid={`analytics-range-${d}`} size="sm" variant={days === d ? "default" : "outline"}
                onClick={() => setDays(d)} className="rounded-xl">{d}d</Button>
            ))}
            <Button data-testid="analytics-refresh" size="sm" variant="outline" onClick={() => loadSummary()} className="rounded-xl"><RefreshCw size={14} /></Button>
          </div>
        }
      >
        {!summary ? (
          <div className="text-sm text-muted-foreground">Loading analytics…</div>
        ) : (
          <div className="space-y-5" data-testid="analytics-dashboard">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat icon={Eye} label={`Views (${days}d)`} value={summary.total_views} />
              <Stat icon={Eye} label="Views today" value={summary.today_views} />
              <Stat icon={MousePointerClick} label="Total events" value={summary.total_events} />
              <Stat icon={Smartphone} label="Mobile share" value={`${summary.total_views ? Math.round(((summary.devices.find((d) => d.device === "mobile")?.views || 0) / summary.total_views) * 100) : 0}%`} />
            </div>

            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={summary.series}>
                  <defs>
                    <linearGradient id="pv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} width={30} />
                  <Tooltip />
                  <Area type="monotone" dataKey="views" stroke="#2563eb" strokeWidth={2} fill="url(#pv)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {[
                { title: "Top Pages", rows: summary.top_pages.map((p) => [p.path, p.views]) },
                { title: "Top Referrers", rows: summary.top_referrers.map((p) => [p.source, p.views]) },
                { title: "Events", rows: summary.events.map((p) => [p.name, p.count]) },
              ].map((box) => (
                <div key={box.title} className="rounded-xl border border-border p-4">
                  <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">{box.title}</div>
                  {box.rows.length === 0 ? <div className="text-xs text-muted-foreground">No data yet</div> : (
                    <ul className="space-y-1.5">
                      {box.rows.map(([k, v]) => (
                        <li key={k} className="flex justify-between gap-3 text-sm">
                          <span className="truncate text-slate-600 dark:text-slate-300">{k}</span>
                          <span className="font-medium tabular-nums">{v}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            <Button data-testid="analytics-reset" variant="outline" onClick={reset} className="rounded-xl text-red-600">
              <Trash2 size={14} className="mr-1" /> Clear analytics data
            </Button>
          </div>
        )}
      </Panel>

      <Panel title="Tracking IDs" subtitle="Google Analytics, Tag Manager and other trackers." actions={saveBtn}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Google Analytics 4 Measurement ID" hint="G-XXXXXXXXXX">
            <Input data-testid="an-ga4" value={data.ga4_id || ""} onChange={(e) => set("ga4_id", e.target.value)} placeholder="G-XXXXXXXXXX" />
          </Field>
          <Field label="Google Tag Manager ID" hint="GTM-XXXXXXX">
            <Input data-testid="an-gtm" value={data.gtm_id || ""} onChange={(e) => set("gtm_id", e.target.value)} placeholder="GTM-XXXXXXX" />
          </Field>
          <Field label="Meta (Facebook) Pixel ID">
            <Input data-testid="an-fb" value={data.fb_pixel_id || ""} onChange={(e) => set("fb_pixel_id", e.target.value)} />
          </Field>
          <Field label="Microsoft Clarity ID">
            <Input data-testid="an-clarity" value={data.clarity_id || ""} onChange={(e) => set("clarity_id", e.target.value)} />
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div>
              <div className="text-sm font-medium">Internal analytics</div>
              <p className="text-xs text-muted-foreground">Track page views in your own database.</p>
            </div>
            <Switch data-testid="an-internal-toggle" checked={!!data.internal_analytics} onCheckedChange={(v) => set("internal_analytics", v)} />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div>
              <div className="text-sm font-medium">Respect Do-Not-Track</div>
              <p className="text-xs text-muted-foreground">Skip trackers for users with DNT enabled.</p>
            </div>
            <Switch data-testid="an-dnt-toggle" checked={!!data.respect_dnt} onCheckedChange={(v) => set("respect_dnt", v)} />
          </div>
        </div>
      </Panel>

      <Panel title="Site Verification" subtitle="Meta tags added to every page for search engine ownership checks." actions={saveBtn}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Google Search Console" hint="Only the content value of the meta tag">
            <Input data-testid="an-gsc" value={data.google_site_verification || ""} onChange={(e) => set("google_site_verification", e.target.value)} />
          </Field>
          <Field label="Bing Webmaster (msvalidate.01)">
            <Input data-testid="an-bing" value={data.bing_verification || ""} onChange={(e) => set("bing_verification", e.target.value)} />
          </Field>
          <Field label="Yandex Verification">
            <Input data-testid="an-yandex" value={data.yandex_verification || ""} onChange={(e) => set("yandex_verification", e.target.value)} />
          </Field>
          <Field label="Pinterest Verification">
            <Input data-testid="an-pinterest" value={data.pinterest_verification || ""} onChange={(e) => set("pinterest_verification", e.target.value)} />
          </Field>
        </div>
      </Panel>

      <Panel title="Custom Scripts" subtitle="Raw HTML injected into the page. Use with care." actions={saveBtn}>
        <Field label="Head code" hint="Injected at the end of <head> on public pages.">
          <Textarea data-testid="an-head-code" rows={4} className="font-mono text-xs" value={data.custom_head_code || ""} onChange={(e) => set("custom_head_code", e.target.value)} />
        </Field>
        <Field label="Body code" className="mt-4" hint="Injected at the end of <body> on public pages.">
          <Textarea data-testid="an-body-code" rows={4} className="font-mono text-xs" value={data.custom_body_code || ""} onChange={(e) => set("custom_body_code", e.target.value)} />
        </Field>
      </Panel>
    </div>
  );
}
